import { Link } from 'react-router';
import {
  addDays,
  formatPeso,
  inRange,
  nextPayday,
  paydaysInRange,
  resolveTiedSchedules,
  type EffectivePayday,
} from '@tahi/core';
import { useState } from 'react';
import { deleteIncomeSource, restoreIncomeSource } from '../../../data/commands/budget';
import { useCommandEnv } from '../../../data/hooks';
import { Button, Money, ScreenHeader, showToast } from '../../../ui';
import { copy } from '../copy';
import { duePaydaysOf, liveSources } from '../derive';
import { dateWithYear, dayShort, scheduleSummary } from '../format';
import { useBudgetToday } from '../hooks';
import { CARD, FOCUS, QUIET_LINK, SCREEN } from '../styles';

const BACK = { to: '/m/budget', label: copy.moduleName };

function PaydayLink({ p, amount, tag }: { p: EffectivePayday; amount: number; tag?: string }) {
  return (
    <li>
      <Link
        to={`/m/budget/payday/${p.sourceId}/${p.nominalDate}`}
        aria-label={copy.paydayRow(p.sourceName, dayShort(p.date), amount)}
        className={`flex min-h-11 items-center justify-between gap-3 ${FOCUS}`}
      >
        <span className="text-fg">
          {p.sourceName} · {dayShort(p.date)}
          {tag ? ` · ${tag}` : ''}
        </span>
        <Money minor={amount} cents="auto" animate={false} />
      </Link>
    </li>
  );
}

/** v0.5: delete a source right from the list (tap twice), with Undo. */
function DeleteSource({ id, name }: { id: string; name: string }) {
  const env = useCommandEnv();
  const [armed, setArmed] = useState(false);
  return (
    <Button
      variant={armed ? 'destructive' : 'ghost'}
      aria-label={armed ? copy.confirmDelete : copy.deleteSource(name)}
      onClick={() => {
        if (!armed) return setArmed(true);
        void deleteIncomeSource(env, id).then(() =>
          showToast({
            message: copy.deletedSource(name),
            action: { label: copy.undo, onPress: () => void restoreIncomeSource(env, id) },
          }),
        );
      }}
    >
      {armed ? copy.confirmDelete : copy.delete}
    </Button>
  );
}

export function IncomeScreen() {
  const s = useBudgetToday();
  if (!s) {
    return (
      <main className={SCREEN}>
        <ScreenHeader title={copy.income} back={BACK} />
        <p className="text-muted">{copy.loading}</p>
      </main>
    );
  }
  const due = duePaydaysOf(s);
  const sources = liveSources(s);
  const recentRange = { start: addDays(s.today, -60), end: s.today };
  const acted = paydaysInRange(s.userId, s.sources, s.rows, recentRange)
    .filter((p) => p.status !== 'expected')
    .reverse();
  const paydayLedgerIds = new Set(
    s.rows.map((r) => r.ledgerEntryId).filter((id): id is string => id !== null),
  );
  const other = s.entries
    .filter(
      (e) => e.kind === 'income' && !paydayLedgerIds.has(e.id) && inRange(e.date, recentRange),
    )
    .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));

  return (
    <main className={SCREEN}>
      <ScreenHeader title={copy.income} back={BACK} />
      <nav aria-label={copy.income} className="flex flex-wrap gap-2">
        <Link to="/m/budget/income/new?kind=salary" className={QUIET_LINK}>
          {copy.addSalary}
        </Link>
        <Link to="/m/budget/income/new?kind=bonus" className={QUIET_LINK}>
          {copy.addBonus}
        </Link>
        <Link to="/m/budget/income/other" className={QUIET_LINK}>
          {copy.addOtherIncome}
        </Link>
      </nav>

      {due.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h2 className="text-[20px] font-bold text-fg">{copy.waiting}</h2>
          <ul aria-label={copy.waiting} className="divide-y divide-divider">
            {due.map((p) => (
              <PaydayLink key={p.id} p={p} amount={p.defaultAmountMinor} />
            ))}
          </ul>
        </section>
      ) : null}

      <section className="flex flex-col gap-2">
        <h2 className="text-[20px] font-bold text-fg">{copy.sources}</h2>
        {sources.length === 0 ? (
          <p className="text-muted">{copy.noSources}</p>
        ) : (
          <ul aria-label={copy.sources} className="flex flex-col gap-2.5">
            {sources.map((src) => {
              const next = nextPayday(
                s.userId,
                resolveTiedSchedules(s.sources).filter((x) => x.id === src.id),
                s.rows,
                s.today,
              );
              return (
                <li key={src.id} className={`${CARD} flex items-start justify-between gap-2`}>
                  <Link
                    to={`/m/budget/income/${src.id}`}
                    className={`flex min-w-0 flex-1 flex-col gap-1 ${FOCUS}`}
                  >
                    <span className="font-medium text-fg">{src.name}</span>
                    <span className="text-sm text-muted">
                      {scheduleSummary(src.schedule)} ·{' '}
                      {src.defaultAmounts.map((a) => formatPeso(a)).join(' / ')}
                    </span>
                    {next ? (
                      <span className="text-sm text-muted">
                        {copy.nextOn(dayShort(next.date), next.defaultAmountMinor)}
                      </span>
                    ) : null}
                    {src.endDate ? (
                      <span className="text-sm text-muted">
                        {copy.endsOn(dateWithYear(src.endDate))}
                      </span>
                    ) : null}
                  </Link>
                  <DeleteSource id={src.id} name={src.name} />
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-[20px] font-bold text-fg">{copy.recent}</h2>
        {acted.length + other.length === 0 ? (
          <p className="text-muted">{copy.nothingHere}</p>
        ) : (
          <ul aria-label={copy.recent} className="divide-y divide-divider">
            {acted.map((p) => (
              <PaydayLink
                key={p.id}
                p={p}
                amount={p.status === 'received' ? (p.amountMinor ?? 0) : p.defaultAmountMinor}
                tag={p.status === 'skipped' ? copy.tagSkipped : undefined}
              />
            ))}
            {other.map((e) => (
              <li key={e.id}>
                <Link
                  to={`/m/budget/income/other/${e.id}`}
                  className={`flex min-h-11 items-center justify-between gap-3 ${FOCUS}`}
                >
                  <span className="text-fg">
                    {e.note ?? copy.otherIncomeLabel} · {dayShort(e.date)}
                  </span>
                  <Money minor={e.amountMinor} cents="auto" animate={false} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}
